/**
 * Statements (onlyfans.com/my/statements, CRM statements/statements.js): the
 * pure part — request bodies, paging, the gateway envelope, the withdrawal
 * outcome, OnlyFans' stats buckets and the formatting. Every number comes
 * straight from the OnlyFans endpoint the real page uses; nothing is derived
 * client-side except sums the CRM page also makes.
 */

import type { CrmRequest } from '../CrmRequest'

export const PAGE_LIMIT = 10

export type Section = 'earnings' | 'payouts' | 'stats' | 'chargebacks' | 'referrals'
export type ListSection = Exclude<Section, 'stats'>

export const SECTIONS: { id: Section; time: 'local' | 'utc' }[] = [
  { id: 'earnings', time: 'local' },
  { id: 'payouts', time: 'local' },
  { id: 'stats', time: 'utc' },
  { id: 'chargebacks', time: 'local' },
  { id: 'referrals', time: 'local' },
]

export const CHARGEBACK_PERIODS = [7, 30, 90, 365] as const

/** OnlyFans' category keys in /payouts/stats, in its display order. Unknown keys still get a row. */
export const CATEGORIES = [
  { id: 'subscriptions', keys: ['subscribes', 'subscriptions'] },
  { id: 'tips', keys: ['tips'] },
  { id: 'posts', keys: ['post', 'posts'] },
  { id: 'messages', keys: ['chat_messages', 'messages'] },
  { id: 'referrals', keys: ['ref', 'referrals', 'referral'] },
  { id: 'streams', keys: ['stream', 'streams'] },
] as const

export type Raw = Record<string, unknown>

export interface PeriodOption {
  code: string
  name?: string
}

/** /v1/payouts/balances as OnlyFans answers it (the fields the page reads). */
export interface Balances {
  currency?: string
  payoutAvailable?: number | string
  payoutPending?: number | string
  minPayoutSumm?: number | string
  maxPayoutSumm?: number | string
  manualPayoutPendingDays?: number | string
  withdrawalPeriod?: string
  withdrawalPeriodOptions?: PeriodOption[]
}

export interface Receive {
  canReceiveManualPayout?: boolean
}

// ── Gateway envelope ─────────────────────────────────────────────────────

const asObj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {})

/** `data` of a gateway envelope ({ok|operation, data}), else the body itself (CRM statementsCall). */
export function unwrapGateway<T = unknown>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in body && ('ok' in body || 'operation' in body)) {
    return (body as { data: unknown }).data as T
  }
  return body as T
}

/** The CRM's formatUserFacingError, minus its phrase table: no ids, no "Gateway 502:", no JSON. */
export function userFacingError(raw: unknown, fallback: string): string {
  let s = String(raw ?? '').trim()
  if (!s) return fallback
  s = s.replace(/\s*\[[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\]/gi, '')
  s = s.replace(/^Gateway\s*\d+\s*:\s*/i, '')
  s = s.replace(/^(HTTP|Error)\s*\d+\s*:?\s*/i, '').trim()
  if (!s || /^Gateway\s/i.test(s) || /^HTTP\s*\d/i.test(s) || /^[0-9a-f-]{36}$/i.test(s)) return fallback
  if (s.length > 140 || /^\{.*\}$/.test(s) || s.includes('"ok"')) return fallback
  return s
}

/** The CRM's extractGatewayError: a message only for a failed envelope (`ok: false`), else null. */
export function gatewayError(body: unknown, fallback = 'Request failed'): string | null {
  const b = asObj(body)
  if (b.ok !== false) return null
  if (typeof b.data === 'string' && b.data.includes('Cloudflare')) return 'Blocked by Cloudflare upstream'
  const payload = b.data
  let fromPayload = ''
  if (typeof payload === 'string') fromPayload = payload.trim()
  else if (payload && typeof payload === 'object') {
    const p = payload as Raw
    const nested = asObj(p.error).message
    fromPayload =
      [nested, p.detail, p.message, p.error].map((v) => (typeof v === 'string' ? v.trim() : '')).find(Boolean) ?? ''
  }
  const direct = [b.detail, b.error, b.message].map((v) => (typeof v === 'string' ? v.trim() : '')).find(Boolean) ?? ''
  const op = typeof b.operation === 'string' ? b.operation.trim() : ''
  return userFacingError(fromPayload || direct || op, fallback)
}

// ── Balances ─────────────────────────────────────────────────────────────

const num = (v: unknown) => Number(v)

export function minPayout(b: Balances | null): number {
  const min = num(b?.minPayoutSumm)
  return Number.isFinite(min) && min > 0 ? min : 20
}

/** The largest whole-dollar withdrawal: the available balance, capped by OnlyFans' maximum. */
export function maxWithdrawal(b: Balances | null): number {
  const available = num(b?.payoutAvailable) || 0
  const max = num(b?.maxPayoutSumm)
  const cap = Number.isFinite(max) && available > max ? max : available
  return Math.max(0, Math.floor(cap))
}

export function periodHintKey(period: string): 'minManual' | 'autoWeekly' | 'autoMonthly' | 'autoOther' {
  return period === 'manual' ? 'minManual' : period === 'weekly' ? 'autoWeekly' : period === 'monthly' ? 'autoMonthly' : 'autoOther'
}

/** Whole dollars only, as OnlyFans' modal steps by 1: never "clean up" "20.5" into 205. */
export const WHOLE_DOLLARS = /^\d{1,9}$/

export function withdrawalValue(raw: string): number {
  return WHOLE_DOLLARS.test(raw) ? Number(raw) : 0
}

// ── Withdrawal outcome (/v1/payouts/requests/create) ─────────────────────

export type RefusedReason =
  | { type: 'text'; text: string }
  | { type: 'noPermission' }
  | { type: 'verify'; url: string }
  | { type: 'http'; status: number }

export type WithdrawOutcome =
  | { kind: 'ok'; state: string; rejectReason: string }
  | { kind: 'unknown' }
  | { kind: 'refused'; reason: RefusedReason }

const OF_URL = /^https:\/\/([a-z0-9-]+\.)*onlyfans\.com\//i

/** What OnlyFans made of a withdrawal request (CRM statementsClassifyWithdrawal). */
/** Sends a manual payout request (/v1/payouts/requests/create); a lost request is "unknown", never a refusal. */
export async function submitWithdrawal(request: CrmRequest, accountId: string, amount: number, idempotencyKey: string): Promise<WithdrawOutcome> {
  try {
    const { status, data } = await request<unknown>('/v1/payouts/requests/create', {
      body: { account_id: accountId, withdrawal_amount: amount, idempotency_key: idempotencyKey },
    })
    return classifyWithdrawal(status, data)
  } catch {
    return { kind: 'unknown' }
  }
}

export function classifyWithdrawal(status: number, data: unknown): WithdrawOutcome {
  const gateway = asObj(data)
  const payload = gateway.data
  const errorObj = payload && typeof payload === 'object' ? asObj((payload as Raw).error) : null

  if (status >= 500 || errorObj?.outcomeUnknown) return { kind: 'unknown' }
  if (status === 200 && gateway.ok !== false) {
    const list = asObj(payload).list
    const result = Array.isArray(list) ? asObj(list[0]) : {}
    return { kind: 'ok', state: String(result.state || 'new'), rejectReason: plainText(result.rejectReason) }
  }
  if (status === 403 && !('ok' in gateway)) {
    const detail = typeof gateway.detail === 'string' && gateway.detail.trim() ? gateway.detail : ''
    return { kind: 'refused', reason: detail ? { type: 'text', text: detail } : { type: 'noPermission' } }
  }
  const verifyUrl = asObj(errorObj?.payload).url
  if (gateway.status_code === 402 && typeof verifyUrl === 'string' && OF_URL.test(verifyUrl)) {
    return { kind: 'refused', reason: { type: 'verify', url: verifyUrl } }
  }
  const message = gatewayError(gateway) || (typeof gateway.detail === 'string' && gateway.detail) || ''
  return { kind: 'refused', reason: message ? { type: 'text', text: message } : { type: 'http', status } }
}

// ── Paged lists ──────────────────────────────────────────────────────────

export type Cursor = string | number | null

export interface ListState {
  /** A new id is a new list: a slow page of the old one is dropped. */
  id: number
  rows: Raw[]
  cursor: Cursor
  hasMore: boolean
  loading: boolean
  loaded: boolean
  /** Null: no error; '' an error without a message. */
  error: string | null
}

let listIds = 0
export function emptyList(): ListState {
  listIds += 1
  return { id: listIds, rows: [], cursor: null, hasMore: true, loading: false, loaded: false, error: null }
}

const pad = (n: number) => String(n).padStart(2, '0')
export const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const hms = (d: Date) => [d.getHours(), d.getMinutes(), d.getSeconds()].map(pad).join(':')

/** The chargeback window: `days` back from midnight, up to now, in local time. */
export function chargebackRange(days: number, now = new Date()) {
  const start = new Date(now)
  start.setDate(start.getDate() - days)
  return { start_date: `${ymd(start)} 00:00:00`, end_date: `${ymd(now)} ${hms(now)}` }
}

/** The OnlyFans call for the next page of a list (earnings by marker, the rest by offset). */
export function listRequest(section: ListSection, cursor: Cursor, days: number, now = new Date()): { path: string; body: Raw } {
  switch (section) {
    case 'earnings':
      return { path: '/v1/payouts/transactions', body: cursor ? { limit: PAGE_LIMIT, marker: cursor } : { limit: PAGE_LIMIT } }
    case 'payouts':
      return { path: '/v1/payouts/requests', body: { limit: PAGE_LIMIT, offset: cursor || 0 } }
    case 'referrals':
      return { path: '/v1/referrals/payout-requests', body: { limit: PAGE_LIMIT, offset: cursor || 0 } }
    case 'chargebacks':
      return { path: '/v1/payouts/chargebacks', body: { limit: PAGE_LIMIT, offset: cursor || 0, ...chargebackRange(days, now) } }
  }
}

/** The list after one more page arrived. */
export function appendPage(section: ListSection, list: ListState, body: unknown): Pick<ListState, 'rows' | 'cursor' | 'hasMore'> {
  const b = asObj(body)
  const rows = Array.isArray(b.list) ? (b.list as unknown[]).map(asObj) : []
  if (section === 'earnings') {
    const cursor = (b.nextMarker as Cursor | undefined) ?? null
    return { rows: list.rows.concat(rows), cursor, hasMore: Boolean(b.hasMore) && cursor !== null && rows.length > 0 }
  }
  return { rows: list.rows.concat(rows), cursor: (Number(list.cursor) || 0) + rows.length, hasMore: rows.length >= PAGE_LIMIT }
}

// ── Text from OnlyFans HTML ──────────────────────────────────────────────

function parseHtml(html: string): Document | null {
  try {
    return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  } catch {
    return null
  }
}

export function plainText(html: unknown): string {
  if (!html) return ''
  const doc = parseHtml(String(html))
  return (doc?.body.textContent ?? String(html)).replace(/\s+/g, ' ').trim()
}

export interface DescPart {
  text: string
  /** The fan's name: opens their chat. */
  fanId?: string
}

/**
 * OnlyFans descriptions are HTML ("Tip from <a href=…>Name</a>"): keep the
 * text and turn the fan's name into a link to their chat. Null: no
 * description (the page shows the operation type instead).
 */
export function descriptionParts(row: Raw): DescPart[] | null {
  const raw = String(row?.description ?? '').trim()
  if (!raw) return null
  const fanId = asObj(row.user).id ? String(asObj(row.user).id) : ''
  const doc = parseHtml(raw)
  if (!doc) return [{ text: plainText(raw) }]
  const parts: DescPart[] = []
  doc.body.childNodes.forEach((node) => {
    const text = node.textContent ?? ''
    if (node.nodeType === Node.ELEMENT_NODE && fanId && text.trim()) parts.push({ text: text.trim(), fanId })
    else if (text) parts.push({ text })
  })
  return parts.length ? parts : [{ text: plainText(raw) }]
}

export function descriptionType(row: Raw): string {
  const type = asObj(row?.descriptionDetails).type
  return typeof type === 'string' ? type : ''
}

// ── Earning statistics (/v1/payouts/stats) ───────────────────────────────

export interface StatPoint {
  time: number
  net: number
  gross: number
}

export interface CategoryRow {
  /** A known OnlyFans category (translated by the page), else null. */
  category: string | null
  /** The OnlyFans key, title-cased, for an unknown category. */
  label: string
  tone: string
  points: StatPoint[]
  net: number
  gross: number
}

export function titleCase(key: string): string {
  return String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function toPoints(v: unknown): StatPoint[] {
  return Array.isArray(v) ? v.map((p) => ({ time: Number(asObj(p).time), net: Number(asObj(p).net) || 0, gross: Number(asObj(p).gross) || 0 })) : []
}

function categoryRow(category: string | null, label: string, tone: string, points: unknown, total: unknown): CategoryRow {
  const list = toPoints(points)
  const t = total && typeof total === 'object' ? asObj(total) : null
  return {
    category,
    label,
    tone,
    points: list,
    net: t ? Number(t.total_net) || 0 : list.reduce((s, p) => s + p.net, 0),
    gross: t ? Number(t.total_gross) || 0 : list.reduce((s, p) => s + p.gross, 0),
  }
}

/** A bucket ({tips: [...], chat_messages: [...], total_net, …}) on OnlyFans' fixed category rows, unknown keys kept. */
export function categoryRows(bucket: Raw | null, totals: Raw | null): CategoryRow[] {
  const used = new Set(['total_net', 'total_gross', 'all'])
  const rows = CATEGORIES.map((cat, index) => {
    const key = cat.keys.find((k) => bucket?.[k] !== undefined || totals?.[k] !== undefined)
    cat.keys.forEach((k) => used.add(k))
    return categoryRow(cat.id, '', `s${index + 1}`, key ? bucket?.[key] : undefined, key ? totals?.[key] : undefined)
  })
  const extra = new Set([...Object.keys(bucket ?? {}), ...Object.keys(totals ?? {})].filter((k) => !used.has(k)))
  extra.forEach((key) => rows.push(categoryRow(null, titleCase(key), 'other', bucket?.[key], totals?.[key])))
  return rows
}

export interface StatsCard {
  /** 'all', or the month's start in Unix seconds as OnlyFans keys it. */
  id: string
  /** Null for "All time", else the month's first day (UTC). */
  month: Date | null
  net: number
  gross: number
  rows: CategoryRow[]
  range: { from: number; to: number } | null
}

/** "All time" plus one card per month, newest first. Null when OnlyFans has nothing yet. */
export function statsCards(list: unknown, now = Date.now()): StatsCard[] | null {
  const data = asObj(list)
  const months = asObj(data.months)
  const monthKeys = Object.keys(months).sort((a, b) => Number(b) - Number(a))
  const totals = data.total && typeof data.total === 'object' ? asObj(data.total) : null
  if (!monthKeys.length && !totals) return null

  const allBucket: Raw = {}
  monthKeys.forEach((mk) => {
    Object.entries(asObj(months[mk])).forEach(([key, value]) => {
      if (Array.isArray(value)) allBucket[key] = ((allBucket[key] as unknown[]) ?? []).concat(value)
    })
  })
  const allRows = categoryRows(allBucket, totals)
  const all = totals?.all ? asObj(totals.all) : null
  const firstMonth = monthKeys[monthKeys.length - 1]
  const cards: StatsCard[] = [
    {
      id: 'all',
      month: null,
      net: all ? Number(all.total_net) || 0 : allRows.reduce((s, r) => s + r.net, 0),
      gross: all ? Number(all.total_gross) || 0 : allRows.reduce((s, r) => s + r.gross, 0),
      rows: allRows,
      range: firstMonth ? { from: Number(firstMonth) * 1000, to: now } : null,
    },
  ]
  monthKeys.forEach((mk) => {
    const bucket = asObj(months[mk])
    const start = new Date(Number(mk) * 1000)
    const end = Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)
    cards.push({
      id: mk,
      month: start,
      net: Number(bucket.total_net) || 0,
      gross: Number(bucket.total_gross) || 0,
      rows: categoryRows(bucket, null),
      range: { from: start.getTime(), to: Math.min(end, now) },
    })
  })
  return cards
}

export interface ChartModel {
  times: number[]
  series: CategoryRow[]
  /** Running net per series at each time. */
  cumulative: number[][]
  max: number
}

/** Cumulative net per category over time: one line per category that earned. Null: nothing to draw. */
export function chartModel(rows: CategoryRow[]): ChartModel | null {
  const series = rows.filter((r) => r.points.length && r.points.some((p) => p.net))
  if (!series.length) return null
  const times = [...new Set(series.flatMap((s) => s.points.map((p) => p.time)))].filter(Number.isFinite).sort((a, b) => a - b)
  if (!times.length) return null
  const cumulative = series.map((s) => {
    const byTime = new Map<number, number>()
    s.points.forEach((p) => byTime.set(p.time, (byTime.get(p.time) ?? 0) + p.net))
    let running = 0
    return times.map((t) => (running += byTime.get(t) ?? 0))
  })
  return { times, series, cumulative, max: Math.max(1, ...cumulative.flat()) }
}

// ── Formatting ───────────────────────────────────────────────────────────

export function intlLocale(lang: string): string {
  const base = String(lang || '').slice(0, 2)
  return base === 'ru' ? 'ru-RU' : base === 'uk' ? 'uk-UA' : base === 'es' ? 'es-ES' : 'en-US'
}

/** Money as OnlyFans shows it ("$1,234.50"), in the account's currency. */
export function money(value: unknown, currency = 'USD', digits = 2): string {
  const n = Number(value)
  const safe = Number.isFinite(n) ? n : 0
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(safe)
  } catch {
    return `$${safe.toFixed(digits)}`
  }
}

/** A row's date and time in local time ("Oct 7, 2026" / "4:05 pm"), or null. */
export function dateParts(value: unknown, locale: string): { day: string; time: string } | null {
  if (!value) return null
  const date = new Date(value as string)
  if (!Number.isFinite(date.getTime())) return null
  return {
    day: date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' }).toLowerCase(),
  }
}

export function utcDate(ms: number, locale: string): string {
  return new Date(ms).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

export function tickDate(seconds: number, locale: string): string {
  return new Date(seconds * 1000).toLocaleDateString(locale === 'en-US' ? 'en-GB' : locale, {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  })
}

export function monthLabel(date: Date, locale: string): string {
  const label = date.toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
  return locale === 'en-US' ? label.replace(' ', ', ') : label.charAt(0).toUpperCase() + label.slice(1)
}

/** "2026-10-07 00:00:00" → "Oct 07, 2026". */
export function shortDate(value: string, locale: string): string {
  const date = new Date(String(value).replace(' ', 'T'))
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString(locale, { month: 'short', day: '2-digit', year: 'numeric' }) : String(value)
}

/** The browser's offset as OnlyFans labels it: "UTC+03:00". */
export function timezoneLabel(date = new Date()): string {
  const offset = -date.getTimezoneOffset()
  const abs = Math.abs(offset)
  return `UTC${offset >= 0 ? '+' : '-'}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

export function trimNumber(value: number): string {
  return Number(value).toFixed(2).replace(/\.?0+$/, '')
}

/** One idempotency key per opened modal / change: a double click never becomes a second request. */
export function newKey(prefix: string): string {
  const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `${prefix}-${id}`
}

export function referralUrl(ofUserId: string): string {
  return ofUserId ? `https://onlyfans.com?ref=${encodeURIComponent(ofUserId)}` : ''
}
